/**
 * @file lib/form-export.ts
 * @description 表單實例「下載 PDF / 列印」共用邏輯（客戶 PPT S49②，序4）。
 *
 * 由現場稽核（PhaseField）與表單填寫頁共用：有 WiFi（在線）時把填寫好的表單
 * 導出 PDF 下載或直接喚起瀏覽器列印；離線時由呼叫端禁用入口（本模組本身只負責
 * 「已在線」前提下的取檔 + 下載 / 列印動作）。
 *
 * 後端端點：`POST /api/form-instances/:id/export?format=pdf` 直接回傳 PDF 檔流
 * （`Content-Type: application/pdf`）；失敗回 errorResponse JSON。
 */

/** 導出錯誤：帶後端錯誤碼（供判斷是否回落 HTML） */
class FormExportError extends Error {
  constructor(
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

/**
 * 調用後端導出端點取得檔案 Blob；失敗時解析 errorResponse 給出可讀訊息。
 *
 * @param instanceId 表單實例 ID
 * @param format     導出格式
 * @throws FormExportError 帶可讀 message 與後端錯誤碼
 */
async function fetchFormExportBlob(instanceId: number, format: 'pdf' | 'html'): Promise<Blob> {
  const resp = await fetch(`/api/form-instances/${instanceId}/export?format=${format}`, {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!resp.ok) {
    // 錯誤響應是 errorResponse 包裝的 JSON，盡量取出可讀 message
    let detail = `HTTP ${resp.status}`;
    let code: string | undefined;
    try {
      const body = (await resp.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code;
      if (code === 'NO_DOCX_TEMPLATE') {
        detail = '此表單模板未配置 DOCX 模板，無法導出 PDF';
      } else if (body.error?.message) {
        detail = body.error.message;
      }
    } catch {
      /* 非 JSON 響應（如網關 502），保留 HTTP 狀態碼提示即可 */
    }
    throw new FormExportError(detail, code);
  }
  return resp.blob();
}

/**
 * 取得表單實例的 PDF Blob（下載 / 列印用）。
 *
 * 2026-09-25 回報序52：後端對「未配置 DOCX 模板」的表單已改由 schema 直接生成 PDF；
 * 若伺服器端 PDF 轉檔失敗（PDF_CONVERT_FAILED），再回落取「可列印 HTML」——
 * 回傳的 Blob type 為 text/html，triggerPdfDownload / printPdfBlob 會自動以 HTML 處理，
 * 確保所有表單都能下載 / 列印。
 *
 * @throws Error 帶可讀 message（供呼叫端 message.error 展示）
 */
export async function fetchFormPdfBlob(instanceId: number): Promise<Blob> {
  try {
    return await fetchFormExportBlob(instanceId, 'pdf');
  } catch (err) {
    if (err instanceof FormExportError && err.code === 'PDF_CONVERT_FAILED') {
      console.warn('[form-export] pdf convert failed, fallback to printable html', err);
      return fetchFormExportBlob(instanceId, 'html');
    }
    throw err;
  }
}

/**
 * 觸發瀏覽器下載 Blob（PDF；HTML 回落時自動把副檔名改為 .html）。
 * @param blob PDF（或回落的 HTML）內容
 * @param filename 下載檔名（含 .pdf 副檔名）
 */
export function triggerPdfDownload(blob: Blob, filename: string): void {
  const finalName = blob.type.startsWith('text/html')
    ? filename.replace(/\.pdf$/i, '') + '.html'
    : filename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = finalName;
  a.click();
  URL.revokeObjectURL(url);
}

/** 列印用隱藏 iframe 的 DOM id：同一時間只保留一個，重複列印時先移除舊的 */
const PRINT_IFRAME_ID = 'zhcb-form-print-iframe';

/**
 * 用隱藏 iframe 載入 PDF Blob 並喚起瀏覽器列印對話框。
 *
 * 選 iframe 而非 `window.open(url) + print()` 的原因：
 * - 新窗口容易被瀏覽器彈窗攔截（用戶點擊後的 async fetch 回調中 open 已脫離手勢上下文）；
 * - iframe 常駐當前頁面，無需用戶處理額外窗口，體驗更穩。
 *
 * 注意：iframe 與 blob URL 不能在 print() 後立即清理——列印對話框開啟期間仍引用該文檔，
 * 提前 revoke 會導致列印空白。這裡在下一次列印時才替換舊 iframe。
 *
 * @param blob PDF 內容
 * @param onPrintError 喚起列印失敗時的回調（呼叫端可 message.error 提示）
 */
export function printPdfBlob(blob: Blob, onPrintError?: (err: unknown) => void): void {
  // 移除上一次列印殘留的 iframe，並釋放其 blob URL
  const prev = document.getElementById(PRINT_IFRAME_ID);
  if (prev instanceof HTMLIFrameElement) {
    if (prev.src.startsWith('blob:')) URL.revokeObjectURL(prev.src);
    prev.remove();
  }

  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.id = PRINT_IFRAME_ID;
  iframe.style.display = 'none';
  iframe.src = url;
  iframe.onload = () => {
    // PDF 載入完成後喚起列印；個別瀏覽器需要先 focus 才能正確定位列印目標
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      onPrintError?.(err);
    }
  };
  document.body.appendChild(iframe);
}
